import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import Incentive from '../incentive/incentive.component';
import { getLinesCount } from '../incentive/incentive.utils';
import { areContentItemsEqual, areContextsEqual, getTemplateEntry, ICON_POSITION } from '../utils/templates';
import { ALIGNMENT_VERTICAL } from '../incentive/incentive.constants';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { templates, sizes, imageOptions, ICONS_TYPES } from './jacket.constants';

import {
  getInitialStyle,
  defaultContext,
  getIconViewBox,
  getFocusedStyle,
  unFocusedStyle,
  getIcon,
} from './jacket.style';

/**
 * This item displays programs, movies & series. It's a 3:4 ratio format.
 *
 * @version 2.7.2
 * @see https://zeroheight.com/726d5c282/p/45447a-jacket--v272
 */
export default class Jacket extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        Incentive: {
          type: Incentive,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({ content, config, template, colorScheme, isSearch = false }) {
    const renderingContext = isSearch ? RENDERING_CONTEXT.SEARCH : RENDERING_CONTEXT.GENERAL;
    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;

    const { pictos, image, incentives } = this._item;
    const { screenSize } = this._style;
    const imageUrl = (image && image.src) || '';
    const viewBox = getIconViewBox(screenSize);
    const incentiveLines = getLinesCount(incentives);
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);
    this._style = { screenSize, colorMode: colorScheme };

    this.patch({
      Image: {
        src: imageUrl,
      },
      TopLeftIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_LEFT),
          viewBox,
        },
      },
      TopRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_RIGHT),
          viewBox,
        },
      },
      BottomRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT),
          viewBox,
        },
      },
      Incentive: {
        visible: incentiveLines > 0,
        data: {
          linesCount: incentiveLines,
          w: this._config.w,
          incentives,
          colorMode: this._style.colorMode,
          alignment: ALIGNMENT_VERTICAL,
        },
      },
    });
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Jacket.Templates = templates;
Jacket.Sizes = sizes;
Jacket.ImageOptions = imageOptions;
